import { Component, OnInit, ViewChild } from '@angular/core';
import { SelectItem, SortMeta } from 'primeng/api';
import { ReportingPeriodNames } from '../../shared/reporting-period-names';
import { ReportingPeriods } from '../../shared/reporting-periods';
import { GlobalConstants } from '../../shared/global-constants';
import { GridComponent } from '../../shared/grid/grid.component';
import { NgMessage } from '../../shared/ng-message';
import { SubmissionArtifact } from '../../shared/submission-artifact.model';
import { ViewRollupArtifactResponse } from '../../shared/rollup-artifact.model';
import { DownloadArtifact, FilterArtifacts, Request } from '../../shared/base-artifact.model';
import { GridColumn, GridFilter, ShowFilterEvent } from '../../shared/grid.model';
import { AppMessageService } from '../../services/app-message.service';
import { MessageCodes } from '../../shared/message-codes';
import { dateFormat, fieldof, getArtifactTypeLabel } from '../../shared/string-operations';
import { GridProcessor } from '../../shared/grid.processor';
import { DataService } from '../../services/data.service';
import { StatusTypes } from '../../shared/status-types';
import { BaseComponent } from '../base/base.component';
import { AppContext } from '../../app.context';
import { ArtifactFileFormats } from '../../shared/artifact-file-formats';
import { HttpErrorResponse } from '@angular/common/http';
import { NgResponse } from '../../shared/ng-response';
import { EmptyResponse } from '../../shared/empty-response';
import { AppInsightsService } from '../../services/app-insights.service';
import { TableLazyLoadEvent } from 'primeng/table';
import { CaptchaService } from '../../services/captcha.service';
import { ReferenceDataService } from '../../services/reference-data.service';
import { FiscalYearFormat, FiscalYearReportingPeriodsResponse } from '../../shared/fiscal-year.model';
import { ReportingPeriodStatuses } from '../../shared/reporting-period-statuses';
import { EntityTypesService } from '../../services/entity-types.service';
import { EntityTypes } from '../../shared/entity-types';
import { ArtifactTypes } from '../../shared/artifact-types';

@Component({
  selector: 'home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent extends BaseComponent implements OnInit {
  @ViewChild('grid', { static: true }) grid: GridComponent;

  readonly unknownErrorMessage: NgMessage;
  readonly entityHasNotSubmittedDataInfoMessage: NgMessage;

  loading: boolean;
  data: Array<SubmissionArtifact>;
  cols: Array<GridColumn>;
  availableColumns: Array<GridColumn>;
  selectedColumns: Array<GridColumn>;
  multiSortMeta: Array<SortMeta>;
  globalFilterFields: Array<string>;
  globalFilterMatchMode: string;
  totalRecords: number;

  lazyLoadEvent: TableLazyLoadEvent;
  exporting: boolean = false;
  importing: boolean = false;

  loadingFiscalYears: boolean = true;
  fiscalYearOptions: Array<FiscalYearReportingPeriodsResponse>;

  loadingReportingPeriods: boolean = false;
  reportingPeriodNames: ReportingPeriodNames = new ReportingPeriodNames();
  reportingPeriodOptions: Array<SelectItem>;

  loadingEntityTypes: boolean = false;
  entityTypeOptions: Array<SelectItem<EntityTypes>>;

  loadingEntities: boolean = false;

  rollupDatImport: ViewRollupArtifactResponse;
  rollupDataExtract: ViewRollupArtifactResponse;

  helpUrl = GlobalConstants.DATA_EXPLORER_HELP_PATH;

  displayModal: boolean = false;
  downloadArtifact: DownloadArtifact;
  downloadAllArtifact: SubmissionArtifact;
  token: string;
  captchaSiteKey: string;
  captchaDisabled: boolean;

  filteredFullFiscalYear: string;
  filteredReportingPeriod: ReportingPeriods;
  filteredCDSCode: string;
  filteredEntityType: EntityTypes;

  artifactFileFormats = ArtifactFileFormats;

  constructor(
    readonly appContext: AppContext,
    readonly appInsightsService: AppInsightsService,
    readonly appMessageService: AppMessageService,
    readonly captchaService: CaptchaService,
    readonly dataService: DataService,
    readonly referenceDataService: ReferenceDataService,
    readonly entityTypesService: EntityTypesService) {
    super(appContext, appInsightsService);
    this.unknownErrorMessage = this.appContext.messages[MessageCodes.UnknownError];
    this.entityHasNotSubmittedDataInfoMessage = this.appContext.messages[MessageCodes.SelectedEntityHasNotSubmittedDataOrIsNotRequired];
    this.captchaSiteKey = this.appContext.captchaData.siteKey;
    this.captchaDisabled = this.appContext.captchaData.disabled;
  }

  ngOnInit() {
    this.appMessageService.clear('responseToast');
    this.cols = this.createCols();
    this.multiSortMeta = this.createMultiSortMeta();
    this.availableColumns = this.cols.filter(col => !col.hidden);
    this.selectedColumns = this.cols.filter(col => !col.hidden);
    this.getAvailableFiscalYears();
  }

  fiscalYearChange(fiscalYear: FiscalYearFormat): void {
    this.getAvailableReportingPeriods(fiscalYear);
  }

  reportingPeriodChange(): void {
    this.getAvailableEntityTypes();
  }

  applyFilters(filterArtifacts: FilterArtifacts) {
    this.filteredFullFiscalYear = filterArtifacts.fullFiscalYear;
    this.filteredReportingPeriod = filterArtifacts.reportingPeriod;
    this.filteredCDSCode = filterArtifacts.cdsCode;
    this.filteredEntityType = filterArtifacts.entityType;
    this.getDownloadAllSubmissionArtifact();
    this.getRollupArtifact(ArtifactTypes.Import);
    this.getRollupArtifact(ArtifactTypes.Data);
    this.grid.reset();
  }

  private getAvailableFiscalYears(): void {
    this.loadingFiscalYears = true;

    this.referenceDataService.getActiveFiscalYears().subscribe(ngResponse => {
      if (!ngResponse)
        this.appMessageService.add({ key: 'responseToast', severity: this.unknownErrorMessage.severityLevel.toLocaleString(), summary: this.unknownErrorMessage.text });
      else if (ngResponse.status === StatusTypes.Success) {
        this.fiscalYearOptions = ngResponse.response;
      } else
        this.appMessageService.add({ key: 'responseToast', severity: ngResponse.error.severityLevel.toLocaleString(), summary: ngResponse.error.failureReason });

      this.loadingFiscalYears = false;
    });
  }

  private getAvailableReportingPeriods(fiscalYear: FiscalYearFormat): void {
    this.loadingReportingPeriods = true;

    this.reportingPeriodOptions = new Array<SelectItem<ReportingPeriods>>();
    this.AddReportingPeriodOption(ReportingPeriods.BS1, fiscalYear);
    this.AddReportingPeriodOption(ReportingPeriods.I1, fiscalYear);
    this.AddReportingPeriodOption(ReportingPeriods.I2, fiscalYear);
    this.AddReportingPeriodOption(ReportingPeriods.A, fiscalYear);

    this.loadingReportingPeriods = false;
  }

  private AddReportingPeriodOption(reportingPeriod: ReportingPeriods, fiscalYear: FiscalYearFormat) {
    if (fiscalYear.reportingPeriods[reportingPeriod] == ReportingPeriodStatuses.Active)
      this.reportingPeriodOptions.push({ label: this.reportingPeriodNames[reportingPeriod], value: ReportingPeriods[reportingPeriod] });
  }

  private getAvailableEntityTypes(): void {
    this.loadingEntityTypes = true;

    this.entityTypesService.getEntityTypes().subscribe(ngResponse => {
      if (!ngResponse)
        this.appMessageService.add({ key: 'responseToast', severity: this.unknownErrorMessage.severityLevel.toLocaleString(), summary: this.unknownErrorMessage.text });
      else if (ngResponse.status === StatusTypes.Success) {
        this.entityTypeOptions = ngResponse.response.entityTypes.filter(
          t => t.name !== EntityTypes.StateBoardOfEducation && t.name !== EntityTypes.CaliforniaDepartmentOfEducation)
          .map(entityType => {
            return {
              label: entityType.definition,
              value: entityType.name
            } as SelectItem<EntityTypes>;
          });

        if (ngResponse.response.entityTypes.findIndex(t => t.name === EntityTypes.StateBoardOfEducation) > -1)
          this.entityTypeOptions.push({ label: "State Board of Education Charters", value: EntityTypes.StateBoardOfEducation });

        if (ngResponse.response.entityTypes.findIndex(t => t.name === EntityTypes.CaliforniaDepartmentOfEducation) > -1)
          this.entityTypeOptions.push({ label: "All", value: EntityTypes.CaliforniaDepartmentOfEducation });
      } else
        this.appMessageService.add({ key: 'responseToast', severity: ngResponse.error.severityLevel.toLocaleString(), summary: ngResponse.error.failureReason });

      this.loadingEntityTypes = false;
    });
  }

  private getRollupArtifact(artifactType: ArtifactTypes): void {
    if (artifactType == ArtifactTypes.Import)
      this.rollupDatImport = null;
    else if (artifactType == ArtifactTypes.Data)
      this.rollupDataExtract = null;
    else
      return;

    const request = this.appContext.createRequest(this.createRollupArtifactsRequest(artifactType));
    this.dataService.fetchRollupArtifactItem(request)
      .subscribe(ngResponse => {
        if (!ngResponse)
          this.appMessageService.add({ key: 'responseToast', severity: this.unknownErrorMessage.severityLevel.toLocaleString(), summary: this.unknownErrorMessage.text });
        else if (ngResponse.status === StatusTypes.Success) {
          if (ngResponse.response.hasValue)
            if (artifactType == ArtifactTypes.Import)
              this.rollupDatImport = ngResponse.response;
            else if (artifactType == ArtifactTypes.Data)
              this.rollupDataExtract = ngResponse.response;
        } else
          this.appMessageService.add({ key: 'responseToast', severity: ngResponse.error.severityLevel.toLocaleString(), summary: ngResponse.error.failureReason });
      });
  }

  private getDownloadAllSubmissionArtifact(): void {
    this.downloadAllArtifact = undefined;
    const request = this.appContext.createRequest(
      new Request({
        fullFiscalYear: this.filteredFullFiscalYear,
        reportingPeriod: this.filteredReportingPeriod,
        cdsCode: this.filteredCDSCode,
        includeArtifactTypes: [ArtifactTypes.All]
      }));

    this.dataService.fetchSubmissionArtifactItem(request)
      .subscribe(ngResponse => {
        if (!ngResponse)
          this.appMessageService.add({ key: 'responseToast', severity: this.unknownErrorMessage.severityLevel.toLocaleString(), summary: this.unknownErrorMessage.text });
        else if (ngResponse.status === StatusTypes.Success) {
          if (ngResponse.response.hasValue)
            this.downloadAllArtifact = ngResponse.response;
        } else
          this.appMessageService.add({ key: 'responseToast', severity: ngResponse.error.severityLevel.toLocaleString(), summary: ngResponse.error.failureReason });
      });
  }

  private createCols(): Array<GridColumn> {
    return [
      new GridColumn({
        header: 'Form Name',
        field: fieldof<SubmissionArtifact>('name'),
        filter: new GridFilter()
      }),
      new GridColumn({
        header: 'File Name',
        field: fieldof<SubmissionArtifact>('fileName'),
        filter: new GridFilter(),
        getTooltip: () => 'File Name format: CDS Code_Reporting Period_Fiscal Year_Dataset Number_Form_Form Detail',
        getTooltipStyleClass: 'artifactFileNameTooltip'
      }),
      new GridColumn({
        header: 'Type',
        field: fieldof<SubmissionArtifact>('type'),
        filter: new GridFilter(),
        formatValue: getArtifactTypeLabel
      }),
      new GridColumn({
        header: 'Published Date',
        field: fieldof<SubmissionArtifact>('date'),
        filter: new GridFilter({
          formatValue: dateFormat
        }),
        formatValue: dateFormat,
      }),
    ];
  }

  private createMultiSortMeta(): Array<SortMeta> {
    return new Array<SortMeta>(
      { field: fieldof<SubmissionArtifact>('date'), order: 1 },
      { field: fieldof<SubmissionArtifact>('type'), order: 1 },
      { field: fieldof<SubmissionArtifact>('name'), order: 1 }
    );
  }

  private createRollupArtifactsRequest(artifactType: ArtifactTypes): Request {
    return new Request({
      fullFiscalYear: this.filteredFullFiscalYear,
      reportingPeriod: ReportingPeriods[this.filteredReportingPeriod],
      cdsCode: this.filteredCDSCode,
      includeArtifactTypes: [artifactType],
    });
  }

  private createSubmissionArtifactsRequest(): Request {
    if (!this.filteredFullFiscalYear || 
        !this.filteredReportingPeriod ||
        !this.filteredCDSCode || 
        !this.filteredEntityType)
            return;
            
    return new Request({
      fullFiscalYear: this.filteredFullFiscalYear,
      reportingPeriod: ReportingPeriods[this.filteredReportingPeriod],
      cdsCode: this.filteredCDSCode,
      excludeArtifactTypes: [ArtifactTypes.All]
    });
  }

  lazyLoadData(event: TableLazyLoadEvent): void {
    const request = this.createSubmissionArtifactsRequest();

    if (!request)
        return;

    this.lazyLoadEvent = event;
    this.loading = true;

    const itemsRequest = GridProcessor.createFilterSortPageRequest(
      request,
      event,
      this.cols,
      this.globalFilterFields,
      this.globalFilterMatchMode);

    this.dataService.fetchSubmissionArtifactItems(this.appContext.createRequest(itemsRequest))
      .subscribe(ngResponse => {
        if (!ngResponse)
          this.appMessageService.add({ key: 'responseToast', severity: this.unknownErrorMessage.severityLevel, summary: this.unknownErrorMessage.text });
        else if (ngResponse.status === StatusTypes.Success) {
          ngResponse.response.results.forEach(a => {
            a.icon = this.iconFileFormat(a.fileFormat);
            a.label = this.labelActionFileButton(a.fileFormat);
            a.ariaLabel = this.ariaLabelActionFileButton(a.fileFormat);
            a.loading = false;
          });
          this.data = ngResponse.response.results;
          this.totalRecords = ngResponse.response.totalCount;
        }
        else
          this.appMessageService.add({ key: 'responseToast', severity: ngResponse.error.severityLevel, summary: ngResponse.error.failureReason });
        this.loading = false;
      });
  }

  showFilter(event: ShowFilterEvent): void {
    const request = this.createSubmissionArtifactsRequest();

    if (request && (!event.col.filter.selectedValues || event.col.filter.selectedValues.length === 0)) {
      event.col.filter.loading = true;
    } else {
      event.col.filter.loading = false;            
      return;
    }

    const itemsRequest = this.appContext.createRequest(GridProcessor.createFilterValuesRequest(
        request,
        event,
        this.cols,
        this.globalFilterFields,
        this.globalFilterMatchMode));

    this.dataService.fetchSubmissionArtifactFilterValues(itemsRequest).subscribe(ngResponse => {
      if (!ngResponse)
        this.appMessageService.add({ key: 'responseToast', severity: this.unknownErrorMessage.severityLevel.toLocaleString(), summary: this.unknownErrorMessage.text });
      else if (ngResponse.status === StatusTypes.Success)
        GridProcessor.processFilterValuesResponse(event.col, ngResponse.response);
      else
        this.appMessageService.add({ key: 'responseToast', severity: ngResponse.error.severityLevel.toLocaleString(), summary: ngResponse.error.failureReason });
        event.col.filter.loading = false;
    });
  }

  downloadSubmissionArtifactBlob(artifact: SubmissionArtifact): void {
    this.downloadArtifact = undefined;
    this.appMessageService.clear('responseToast');
    if (artifact) {
      this.downloadArtifact = new DownloadArtifact({ artifact: artifact, isRollup: false });
      if (this.captchaDisabled)
        this.downloadBlob();
      else
        this.setDisplayCaptcha(true);
    } else {
      this.appMessageService.add({ key: 'responseToast', severity: 'info', summary: 'select submission artifact to download' });
    }
  }

  downloadRollupArtifactBlob(artifact: ViewRollupArtifactResponse): void {
    this.downloadArtifact = undefined;
    this.appMessageService.clear('responseToast');
    if (artifact) {
      this.downloadArtifact = new DownloadArtifact({ artifact: artifact, isRollup: true });
      if (this.captchaDisabled)
        this.downloadBlob();
      else
        this.setDisplayCaptcha(true);
    } else {
      this.appMessageService.add({ key: 'responseToast', severity: 'info', summary: 'select rollup artifact to download' });
    }
  }

  private downloadBlob() {
    this.appMessageService.clear('responseToast');
    this.downloadArtifact.artifact.loading = true;

    const service = this.downloadArtifact.isRollup ?
      this.dataService.getRollupArtifactBlob(this.downloadArtifact.artifact.id) :
      this.dataService.getSubmissionArtifactBlob(this.downloadArtifact.artifact.id);

    service.subscribe({
      next: blobResponse => {
        if (!blobResponse)
          this.appMessageService.add({ key: 'responseToast', severity: this.unknownErrorMessage.severityLevel.toLocaleString(), summary: this.unknownErrorMessage.text });
        else {
          const fileName = this.getFileName(blobResponse);
          if (this.downloadArtifact.artifact.fileFormat === ArtifactFileFormats.PDF)
            this.previewFile(blobResponse, fileName || this.downloadArtifact.artifact.fileName);
          else
            this.downloadFile(blobResponse, fileName || this.downloadArtifact.artifact.fileName);
          this.downloadArtifact.artifact.loading = false;
        }
      },
      error: (errorResponse: HttpErrorResponse) => {
        const ngResponse = new NgResponse<EmptyResponse>(errorResponse.error);
        this.appMessageService.add({ key: 'responseToast', severity: ngResponse.error.severityLevel.toLocaleString(), summary: ngResponse.error.failureReason });
        this.downloadArtifact.artifact.loading = false;
      }
    });
  }

  captchaResolved(token: string) {
    //calling the service and passing the token to the service
    this.captchaService.sendToken(token).subscribe({
      next: response => {
        if (!response)
          this.appMessageService.add({ key: 'responseToast', severity: this.unknownErrorMessage.severityLevel.toLocaleString(), summary: this.unknownErrorMessage.text });
        else
          if (response.response.success) {
            this.downloadBlob();
          } else {
            this.appMessageService.add({ key: 'responseToast', severity: 'error', summary: 'Captcha validation failed, please try again' });
          }
      },
      error: (errorResponse: HttpErrorResponse) => {
        const ngResponse = new NgResponse<EmptyResponse>(errorResponse.error);
        this.appMessageService.add({ key: 'responseToast', severity: ngResponse.error.severityLevel.toLocaleString(), summary: ngResponse.error.failureReason });
      },
      complete: () => {
        this.setDisplayCaptcha(false);
      }
    });
  }

  private setDisplayCaptcha(display: boolean) {
    this.displayModal = display && !this.captchaDisabled;
  }

  private iconFileFormat(fileFormat: ArtifactFileFormats): string {
    switch (fileFormat) {
      case ArtifactFileFormats.PDF:
        return 'pi pi-print';
      case ArtifactFileFormats.XLSX:
        return 'pi pi-file-excel';
      default:
        return 'pi pi-file';
    }
  }

  private labelActionFileButton(fileFormat: ArtifactFileFormats): string {
    switch (fileFormat) {
      case ArtifactFileFormats.PDF:
        return 'Print';
      default:
        return 'Download';
    }
  }

  private ariaLabelActionFileButton(fileFormat: ArtifactFileFormats): string {
    switch (fileFormat) {
      case ArtifactFileFormats.PDF:
        return 'Print form';
      default:
        return 'Download file';
    }
  }

  entityType: typeof EntityTypes = EntityTypes;
}